import { SubmitButton } from 'components/common/buttons/SubmitButton';
import ErrorMessage from 'components/common/ErrorMessage';
import React, { useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { fetchAppointmentsInterpreters, inviteInterpretersSelector } from 'slices/inviteInterpreterSlice';
import { Select } from './Select';
import { useTranslation } from 'react-i18next';

function SelectInterpreterModal({
    isOpen,
    onClose,
    onConfirm,
    appointmentId,
    items,
    previewEmailOnSubmit = false,
    endpoint = "assign",
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [loadingInterpreters, setLoadingInterpreters] = useState(false);
    const { allInterpreters } = useSelector(inviteInterpretersSelector);

    const interpreterOptions = useMemo(() => {
        if (!allInterpreters) return [];
        return allInterpreters.map(interpreter => ({
            value: interpreter.id,
            label: interpreter.first_name + ' ' + interpreter.last_name,
        }));
    }, [allInterpreters]);

    const methods = useForm({
        defaultValues: {
            interpreter_id: null,
            agency_interpreter_selection: null,
            agency_interpreter_id: null,
            agency_interpreter_name: '',
            agency_interpreter_code: '',
        }
    });

    const { handleSubmit, formState: { errors }, control, setValue, watch, reset } = methods;

    const selectedInterpreterId = watch('interpreter_id');
    const selectedAgencySelection = watch('agency_interpreter_selection');

    const selectedInterpreterObj = useMemo(() => {
        if (!allInterpreters || !selectedInterpreterId) return null;
        return allInterpreters.find(i => i.id == selectedInterpreterId) || null;
    }, [allInterpreters, selectedInterpreterId]);

    const isAgency = selectedInterpreterObj?.vendor_type === 'agency';

    const agencyInterpreterList = useMemo(() => {
        if (!selectedInterpreterObj) return [];
        return selectedInterpreterObj.agency_interpreters || selectedInterpreterObj.agencyInterpreters || [];
    }, [selectedInterpreterObj]);

    const agencyOptions = useMemo(() => {
        const opts = agencyInterpreterList.map(item => ({
            value: item.id,
            // label: `${item.name}${item.code ? ' (' + item.code + ')' : ''}`,
            label: `${item.name}`,
            raw: item,
        }));
        opts.push({
            value: 'manual',
            label: `+ ${t('selectInterpreter.createNewAgencyInterpreter', 'Create New Agency Interpreter')}`,
        });
        return opts;
    }, [agencyInterpreterList, t]);

    const [interpreterState, setInterpreterState] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            reset({
                interpreter_id: null,
                agency_interpreter_selection: null,
                agency_interpreter_id: null,
                agency_interpreter_name: '',
                agency_interpreter_code: '',
            });
            setInterpreterState(null);
        }
    }, [isOpen, reset]);

    useEffect(() => {
        if (isOpen && appointmentId) {
            setLoadingInterpreters(true);
            dispatch(fetchAppointmentsInterpreters(appointmentId))
                .finally(() => setLoadingInterpreters(false));
        }
    }, [isOpen, appointmentId, dispatch]);

    useEffect(() => {
        if (isOpen && items?.interpreter_id && items?.interpreter) {
            const selected = {
                value: items.interpreter_id,
                label: items.interpreter.first_name + ' ' + items.interpreter.last_name,
            };
            setInterpreterState(selected);
            setValue('interpreter_id', items.interpreter_id);

            if (items.interpreter.vendor_type === 'agency') {
                if (items.agency_interpreter_id) {
                    setValue('agency_interpreter_selection', items.agency_interpreter_id);
                    setValue('agency_interpreter_id', items.agency_interpreter_id);
                } else if (items.agency_interpreter_name) {
                    setValue('agency_interpreter_selection', 'manual');
                    setValue('agency_interpreter_id', null);
                    setValue('agency_interpreter_name', items.agency_interpreter_name || '');
                    setValue('agency_interpreter_code', items.agency_interpreter_code || '');
                }
            }
        }
    }, [isOpen, items, setValue]);

    const actionLabel = endpoint === "reassign" ? t("appointmentView.reassign") : t("appointmentView.assign");

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="modal-medium">
            <form onSubmit={handleSubmit((data) => {
                setLoading(true);
                const payload = {
                    ...data,
                    ...(previewEmailOnSubmit ? { preview_reassign_email: true } : {}),
                };
                if (!isAgency) {
                    delete payload.agency_interpreter_selection;
                    delete payload.agency_interpreter_id;
                    delete payload.agency_interpreter_name;
                    delete payload.agency_interpreter_code;
                }
                onConfirm(payload, endpoint, () => { setLoading(false); });
            })}>
                <ModalHeader className='header_modal align-items-center'>
                    <h5 className="modal-title_u">{actionLabel} {t("selectInterpreter.vendor")}</h5>
                </ModalHeader>
                <ModalBody className='modal_form_body'>
                    <div className='step_cards step_1'>
                        <h2>{actionLabel} {t("selectInterpreter.vendor")}</h2>
                        <div className="row-primary" style={{ marginBottom: 0 }}>
                            <h2>{t("selectInterpreter.selectVendor")}</h2>
                            <Controller
                                name="interpreter_id"
                                control={control}
                                rules={{ required: t("selectInterpreter.selectRequired") }}
                                render={({ field }) => (
                                    <div style={{ width: '100%' }}>
                                        <Select
                                            name="interpreter_id"
                                            options={interpreterOptions}
                                            loading={loadingInterpreters}
                                            value={field.value
                                                ? interpreterOptions.find(opt => opt.value == field.value) ?? null
                                                : interpreterState
                                            }
                                            onChange={(rawValue) => {
                                                // Select component calls onChange with just the raw value (option.value)
                                                const selected = interpreterOptions.find(opt => opt.value == rawValue) ?? null;
                                                setInterpreterState(selected);
                                                field.onChange(rawValue ?? null);
                                                // Reset agency interpreter selections when vendor changes
                                                setValue('agency_interpreter_selection', null);
                                                setValue('agency_interpreter_id', null);
                                                setValue('agency_interpreter_name', '');
                                                setValue('agency_interpreter_code', '');
                                            }}
                                        />
                                        {errors?.interpreter_id?.message && (
                                            <div style={{ marginBottom: 10 }}>
                                                <ErrorMessage text={errors.interpreter_id.message} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                        </div>

                        {isAgency && (
                            <div className="agency-select-interpreter-section w-100" style={{ marginTop: 0 }}>
                                <div className="row-primary">
                                    <h2>{t("selectInterpreter.agencyInterpreter", "Agency Interpreter")}</h2>
                                    <Controller
                                        name="agency_interpreter_selection"
                                        control={control}
                                        rules={{ required: t("selectInterpreter.agencyInterpreterRequired", "Please select or create an Agency Interpreter") }}
                                        render={({ field }) => (
                                            <div style={{ width: '100%' }}>
                                                <Select
                                                    name="agency_interpreter_selection"
                                                    options={agencyOptions}
                                                    value={field.value
                                                        ? agencyOptions.find(opt => opt.value == field.value) ?? null
                                                        : null
                                                    }
                                                    onChange={(rawValue) => {
                                                        field.onChange(rawValue ?? null);
                                                        if (rawValue && rawValue !== 'manual') {
                                                            const selectedObj = agencyInterpreterList.find(a => a.id == rawValue);
                                                            setValue('agency_interpreter_id', rawValue);
                                                            setValue('agency_interpreter_name', selectedObj?.name || '');
                                                            setValue('agency_interpreter_code', selectedObj?.code || '');
                                                        } else {
                                                            setValue('agency_interpreter_id', null);
                                                            setValue('agency_interpreter_name', '');
                                                            setValue('agency_interpreter_code', '');
                                                        }
                                                    }}
                                                />
                                                {errors?.agency_interpreter_selection?.message && (
                                                    <div style={{ marginBottom: 10 }}>
                                                        <ErrorMessage text={errors.agency_interpreter_selection.message} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>

                                {selectedAgencySelection === 'manual' && (
                                    <div className="manual-agency-interpreter-fields" style={{ marginTop: 15 }}>
                                        <div className="row-primary" style={{ marginBottom: 15 }}>
                                            <h2>{t("selectInterpreter.agencyInterpreterCode", "Agency Interpreter ID")}</h2>
                                            <Controller
                                                name="agency_interpreter_code"
                                                control={control}
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        className="input-in type_inp"
                                                        placeholder={t("selectInterpreter.enterAgencyInterpreterCode", "Enter Agency Interpreter ID")}
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="row-primary">
                                            <h2>{t("selectInterpreter.agencyInterpreterName", "Agency Interpreter Name")} *</h2>
                                            <Controller
                                                name="agency_interpreter_name"
                                                control={control}
                                                rules={{ required: t("selectInterpreter.nameRequired", "Agency Interpreter Name is required") }}
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        className="input-in type_inp"
                                                        placeholder={t("selectInterpreter.enterAgencyInterpreterName", "Enter Agency Interpreter Name")}
                                                    />
                                                )}
                                            />
                                            {errors?.agency_interpreter_name?.message && (
                                                <ErrorMessage text={errors.agency_interpreter_name.message} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <SubmitButton btnText={t("translation.submit")} disabled={loadingInterpreters} loader={loading} />
                    <button className="table_delete" type='button' onClick={onClose}>{t("billing.cancel")}</button>
                </ModalFooter>
            </form>
        </Modal>
    );
}

export default SelectInterpreterModal;
